import * as React from "react";
import { RouterProps } from "react-router";

import TabsNavi from "../../../components/molecules/TabsNavi";
import {
  ImportHistoryInterface,
  Operations,
} from "../../../domains/ImportHistory";
import {
  OutletContextProps,
  WithRouterProps,
} from "../../../helpers/RouterHelper";
import enhanceSectionsSettingsPage from "../../../hocs/enhanceSectionsSettingsPage";
import { useErrorDispatcher } from "../../../hooks/useErrorDispatcher";
import SectionInterface from "../../../interfaces/SectionInterface";
import { ImportOperationRouter } from "./components/ImportOperationRouter";
import styles from "./styles.scss";

interface OwnProps extends WithRouterProps<{ importOperation: string }> {
  section: SectionInterface;
}

type Props = OwnProps & RouterProps & OutletContextProps;

const isImportOperation = (
  importOperation: string,
): importOperation is ImportHistoryInterface["operation"] => {
  return Operations.filter(
    // ドリル教材の一括操作画面はないので404ページに遷移させるためのfilter
    (ope) => ope !== "drill_learning_material_insert",
  ).some((ope) => ope === importOperation);
};

const isForbiddenOperation = (
  importOperation: string,
  section: SectionInterface,
): boolean => {
  // LINE利用設定がOFFの場合は、LINE連携向け機能である初回配布資料の保護者向けは表示しない
  if (importOperation === "entry_guides_for_guardians") {
    return !section.guardianLineConnectionUsable;
  }
  return false;
};

const SectionsSettingsImportOperationPage = (props: Props) => {
  React.useEffect(() => {
    props.setActiveMenu("import_histories");
  }, []);

  const { sendNotFound } = useErrorDispatcher();
  const { importOperation } = props.params;
  const section = props.section;

  // React 19 で render 中の dispatch が "Cannot update a component while rendering
  // a different component" warning になるため、404 発火は useEffect に移す。
  // Redux 経由の 404 flow (withErrorPages が state.errors.notFound を検知して
  // NotFoundErrorPage に切り替える) は変わらない。
  // isImportOperation が type guard のため、narrow した値を保持して下流の
  // ImportOperationRouter に渡す (boolean 変数だと narrowing が失われる)。
  const validOperation: ImportHistoryInterface["operation"] | null =
    section &&
    isImportOperation(importOperation) &&
    !isForbiddenOperation(importOperation, section)
      ? importOperation
      : null;
  const shouldSendNotFound = section != null && validOperation === null;

  React.useEffect(() => {
    if (shouldSendNotFound) {
      sendNotFound();
    }
  }, [shouldSendNotFound, sendNotFound]);

  if (!section) {
    return null;
  }
  if (validOperation === null) {
    return null;
  }

  return (
    <div>
      <TabsNavi
        tabs={[
          {
            label: "一括操作",
            link: `/sections/${section.id}/settings/import_histories/operations`,
            active: true,
          },
          {
            label: "操作履歴",
            link: `/sections/${section.id}/settings/import_histories`,
            active: false,
          },
        ]}
      />
      <div className={styles.list}>
        <ImportOperationRouter
          importOperation={validOperation}
          section={section}
        />
      </div>
    </div>
  );
};

const pageInfo = {
  title: "一括操作",
};

const EnhancedPage = enhanceSectionsSettingsPage(
  SectionsSettingsImportOperationPage,
  pageInfo,
  ["import_history:index"],
);

export default EnhancedPage;
